import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { downloadElementAsPdf } from '../../utils/exportPdf';
import { Landmark, CheckCircle2, Save, Download } from 'lucide-react';

// Bank Reconciliation (BRS)
// -------------------------------------------------------------------------
// Compares the app's book balance for a bank account against the bank
// statement. The user ticks the transactions that have "cleared" the bank;
// anything unticked is a deposit-in-transit or an un-cleared cheque. When
//   Statement Balance + uncleared deposits − uncleared withdrawals = Book Balance
// the account is reconciled.

export default function BankReconcile() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();

  const bankAccounts = useMemo(() => state.accounts.filter(a => a.type === 'Bank'), [state.accounts]);
  const [account, setAccount] = useState(bankAccounts[0]?.name || '');
  const [statementBalance, setStatementBalance] = useState('');
  const [statementDate, setStatementDate] = useState('');
  const [cleared, setCleared] = useState(() => new Set());
  const [downloading, setDownloading] = useState(false);

  const savedRecord = useMemo(
    () => (state.bankReconciliations || []).find(r => r.account === account),
    [state.bankReconciliations, account]
  );

  // Load the saved reconciliation whenever the selected account changes.
  useEffect(() => {
    setStatementBalance(savedRecord?.statementBalance != null ? String(savedRecord.statementBalance) : '');
    setStatementDate(savedRecord?.statementDate || '');
    setCleared(new Set(savedRecord?.clearedKeys || []));
  }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

  const bookBalance = bankAccounts.find(a => a.name === account)?.balance || 0;

  // Every transaction that moved money through this bank account.
  const txns = useMemo(() => {
    if (!account) return [];
    const partyName = (id) => state.parties.find(p => p.id === id)?.name || '-';
    const rows = [];
    (state.receipts || []).forEach(r => {
      if (r.mode !== 'Cash' && r.bankAccount === account) {
        rows.push({ key: `receipt:${r.id}`, date: r.date, ref: r.receiptNo, desc: `Receipt · ${partyName(r.partyId)}`, amount: Number(r.amount) || 0 });
      }
    });
    (state.payments || []).forEach(p => {
      if (p.mode !== 'Cash' && p.bankAccount === account) {
        rows.push({ key: `payment:${p.id}`, date: p.date, ref: p.paymentNo, desc: `Payment · ${partyName(p.partyId)}`, amount: -(Number(p.amount) || 0) });
      }
    });
    (state.contraEntries || []).forEach(c => {
      if (c.toAccount === account) rows.push({ key: `contra:${c.id}`, date: c.date, ref: c.contraNo, desc: `Contra · from ${c.fromAccount}`, amount: Number(c.amount) || 0 });
      else if (c.fromAccount === account) rows.push({ key: `contra:${c.id}`, date: c.date, ref: c.contraNo, desc: `Contra · to ${c.toAccount}`, amount: -(Number(c.amount) || 0) });
    });
    return rows.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [account, state.receipts, state.payments, state.contraEntries, state.parties]);

  const toggle = (key) => setCleared(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const allChecked = txns.length > 0 && txns.every(t => cleared.has(t.key));
  const toggleAll = () => setCleared(allChecked ? new Set() : new Set(txns.map(t => t.key)));

  // Uncleared items = deposits in transit / outstanding cheques.
  const unclearedDeposits = txns.filter(t => t.amount > 0 && !cleared.has(t.key)).reduce((s, t) => s + t.amount, 0);
  const unclearedWithdrawals = txns.filter(t => t.amount < 0 && !cleared.has(t.key)).reduce((s, t) => s + Math.abs(t.amount), 0);
  const stmt = Number(statementBalance) || 0;
  const reconciledBalance = stmt + unclearedDeposits - unclearedWithdrawals;
  const difference = bookBalance - reconciledBalance;
  const isReconciled = statementBalance !== '' && Math.abs(difference) < 0.005;

  const handleSave = () => {
    if (!account) return;
    dispatch({
      type: 'UPSERT_BANK_RECON',
      payload: {
        account,
        statementBalance: Number(statementBalance) || 0,
        statementDate: statementDate || null,
        clearedKeys: Array.from(cleared),
      },
    });
    addToast('Reconciliation saved', 'success');
  };

  const handleDownloadPdf = async () => {
    const el = document.getElementById('brs-print-area');
    if (!el || downloading) return;
    setDownloading(true);
    try {
      await downloadElementAsPdf(el, `Bank-Reconciliation-${account}-${statementDate || formatDate(new Date().toISOString())}`);
    } catch (e) {
      console.error('BRS PDF failed:', e);
      addToast('Could not generate the PDF', 'error');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title flex items-center gap-2"><Landmark size={22} className="text-blue-600" /> Bank Reconciliation</h1>
        <div className="flex items-center gap-2">
          <button onClick={handleDownloadPdf} disabled={!account || downloading} className="btn btn-secondary flex items-center gap-2 disabled:opacity-50">
            <Download size={16} /> {downloading ? 'Generating…' : 'Download PDF'}
          </button>
          <button onClick={handleSave} disabled={!account} className="btn btn-primary flex items-center gap-2 disabled:opacity-50">
            <Save size={16} /> Save Reconciliation
          </button>
        </div>
      </div>

      {bankAccounts.length === 0 ? (
        <div className="card text-center text-gray-500 py-10">
          No bank accounts found. Add a bank account in <span className="font-medium">Masters → Account Master</span> (with a Bank/Bank-Accounts group).
        </div>
      ) : (
        <>
          <div className="card mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account</label>
                <select className="input" value={account} onChange={e => setAccount(e.target.value)}>
                  {bankAccounts.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statement Balance (as per bank)</label>
                <input className="input text-right" type="number" step="0.01" value={statementBalance}
                  onChange={e => setStatementBalance(e.target.value)} placeholder="Enter closing balance from bank statement" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statement Date</label>
                <input className="input" type="date" value={statementDate} onChange={e => setStatementDate(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Printable area */}
          <div id="brs-print-area" className="bg-white p-2">
            <div className="text-center mb-4 pb-3 border-b border-gray-300">
              <h2 className="text-lg font-bold text-gray-900">{state.company?.name}</h2>
              <p className="text-sm font-semibold text-gray-700 mt-1">Bank Reconciliation Statement</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Account: {account}{statementDate ? ` · As on ${formatDate(statementDate)}` : ''}
              </p>
            </div>

          {/* Reconciliation summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-3">Reconciliation Statement</h3>
              <div className="space-y-1.5 text-sm">
                <Row label="Balance as per Bank Statement" value={stmt} />
                <Row label="Add: Deposits in transit (uncleared)" value={unclearedDeposits} sign="+" />
                <Row label="Less: Outstanding payments (uncleared)" value={unclearedWithdrawals} sign="-" />
                <div className="flex justify-between pt-2 border-t border-gray-200 font-semibold">
                  <span>Reconciled Balance</span><span>{formatCurrency(reconciledBalance)}</span>
                </div>
                <div className="flex justify-between font-semibold text-gray-600">
                  <span>Balance as per Books</span><span>{formatCurrency(bookBalance)}</span>
                </div>
                <div className={`flex justify-between pt-2 border-t border-gray-200 font-bold ${Math.abs(difference) < 0.005 ? 'text-green-600' : 'text-red-600'}`}>
                  <span>Difference</span><span>{formatCurrency(difference)}</span>
                </div>
              </div>
            </div>
            <div className={`card flex flex-col items-center justify-center text-center ${isReconciled ? 'bg-green-50' : ''}`}>
              {isReconciled ? (
                <>
                  <CheckCircle2 size={40} className="text-green-600 mb-2" />
                  <p className="font-semibold text-green-700">Reconciled ✓</p>
                  <p className="text-sm text-gray-500 mt-1">Book and bank balances match.</p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-800">{formatCurrency(Math.abs(difference))}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {statementBalance === '' ? 'Enter the bank statement balance to reconcile.' : 'Difference remaining — tick cleared transactions to reconcile.'}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Transactions */}
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">Bank Transactions ({txns.length})</h3>
              <span className="text-sm text-gray-500">{cleared.size} cleared</span>
            </div>
            {txns.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">No bank transactions for this account yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs uppercase border-b border-gray-200">
                      <th className="p-2 w-10 text-center">
                        <input type="checkbox" checked={allChecked} onChange={toggleAll} title="Clear all" className="cursor-pointer" />
                      </th>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Ref No</th>
                      <th className="p-2 text-left">Description</th>
                      <th className="p-2 text-right">Deposit</th>
                      <th className="p-2 text-right">Withdrawal</th>
                      <th className="p-2 text-center">Cleared</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map(t => {
                      const isC = cleared.has(t.key);
                      return (
                        <tr key={t.key} className={`border-b border-gray-100 ${isC ? 'bg-green-50/40' : ''}`}>
                          <td className="p-2 text-center">
                            <input type="checkbox" checked={isC} onChange={() => toggle(t.key)} className="cursor-pointer" />
                          </td>
                          <td className="p-2 text-gray-600 whitespace-nowrap">{formatDate(t.date)}</td>
                          <td className="p-2 font-medium text-gray-800">{t.ref}</td>
                          <td className="p-2 text-gray-600">{t.desc}</td>
                          <td className="p-2 text-right text-green-700">{t.amount > 0 ? formatCurrency(t.amount) : ''}</td>
                          <td className="p-2 text-right text-red-600">{t.amount < 0 ? formatCurrency(Math.abs(t.amount)) : ''}</td>
                          <td className="p-2 text-center">
                            {isC && <CheckCircle2 size={16} className="text-green-600 inline" />}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </div>{/* /brs-print-area */}
        </>
      )}
    </div>
  );
}

function Row({ label, value, sign }) {
  return (
    <div className="flex justify-between text-gray-600">
      <span>{label}</span>
      <span>{sign === '-' ? '(' : ''}{formatCurrency(value)}{sign === '-' ? ')' : ''}</span>
    </div>
  );
}
